import React, { useState, useEffect } from "react";
import { dataService } from "../../../../../Services/data/data.service";
import { makeStyles } from "@material-ui/core/styles";
import { Eye, Pencil, Hash, Timer, Repeat } from "lucide-react";
import { parseNewDateToDDMMYYYnoTime, parseNewDateToDDMMYYYWithoutSeconds } from "../../../../../Utils/dateHelper";
import { costBundleTypes } from "../../../../../Config/constants";
import Chip from "../../../../Shared/ui/Chip";
import { useTranslation } from "react-i18next";

function PriceBundleTable({ priceBundles, handleEditBundle, handleViewBundle, searchValue, ...props }) {
  const { t } = useTranslation();
  const [firstLoad, setFirstLoad] = useState(0);
  const classes = useStyles();

  // General
  const [generalError, setGeneralError] = useState(null);
  const [sortedPriceBundles, setSortedPriceBundles] = useState([]);

  useEffect(() => {
    if (priceBundles && priceBundles.length > 0) {
      setSortedPriceBundles(priceBundles);
    } else {
      setSortedPriceBundles([]);
    }
  }, [priceBundles]);

  const handleSorting = (sortType) => {
    console.log("Sorting by: ", sortType);
  };

  const formatPrice = (currencySymbol, priceEntry) => {
    if (priceEntry != null && !isNaN(priceEntry)) {
      return `${currencySymbol}${priceEntry
        .toFixed(3)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ".")
        .replace(/\.(\d{3})$/, ",$1")}`;
    } else {
      return "N/A";
    }
  };

  const formatDate = (date) => {
    if (date) {
      let tempDate = new Date(date);
      return parseNewDateToDDMMYYYWithoutSeconds(tempDate);
    } else {
      return "";
    }
  };

  const formatAccountCell = (account) => {
    if (account) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {account.name}
          {account.isRevenue ? (
            <Chip variant={"success"} size="extraSmall">
              {t('financialCenter.revenue')}
            </Chip>
          ) : (
            <Chip variant={"danger"} size="extraSmall">
              {t('financialCenter.cost')}
            </Chip>
          )}
        </div>
      );
    } else {
      return "N/A";
    }
  };

  const formatTypeCell = (costBundleTypeId, costBundleTypeName) => {
    let icon = null;

    switch (costBundleTypeId) {
      case costBundleTypes.EVENT:
        icon = <Hash size={16} style={{ marginRight: "8px" }} />;
        break;
      case costBundleTypes.TIME:
        icon = <Timer size={16} style={{ marginRight: "8px" }} />;
        break;
      case costBundleTypes.RECURRING:
        icon = <Repeat size={16} style={{ marginRight: "8px" }} />;
        break;
      default:
        icon = null;
    }

    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        {icon}
        {costBundleTypeName}
      </div>
    );
  };

  // Function to highlight search text
  const highlightText = (text, searchValue) => {
    if (!searchValue || !text || searchValue.trim() === "") return text;

    const trimmedSearchValue = searchValue.trim();
    const regex = new RegExp(`(${trimmedSearchValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (part.toLowerCase() === trimmedSearchValue.toLowerCase()) {
        return (
          <mark key={index} style={{ backgroundColor: "var(--color-primary)", color: "#F8F8F8", padding: "0", borderRadius: "2px" }}>
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  return (
    <React.Fragment>
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <thead className={classes.table__header}>
            <tr>
              <th onClick={() => handleSorting("reference")}>{t('financialCenter.reference')}</th>
              <th onClick={() => handleSorting("name")}>{t('common.name')}</th>
              <th onClick={() => handleSorting("description")}>{t('common.description')}</th>
              <th onClick={() => handleSorting("rate")} style={{ textAlign: "right" }}>
                {t('financialCenter.rate')}
              </th>
              <th onClick={() => handleSorting("costBundleTypeName")}>{t('common.type')}</th>
              <th onClick={() => handleSorting("account")}>{t('financialCenter.account')}</th>
              <th onClick={() => handleSorting("company")}>{t('financialCenter.company')}</th>
              <th onClick={() => handleSorting("updatedon")}>{t('financialCenter.updatedOn')}</th>
              <th style={{ minWidth: "16px" }} />
            </tr>
          </thead>
          <tbody className={classes.table__body}>
            {Array.isArray(sortedPriceBundles) && sortedPriceBundles.length > 0 ? (
              sortedPriceBundles.map((bundle, index) => (
                <tr className={classes.table__row} key={index}>
                  <td>{highlightText(bundle.reference, searchValue)}</td>
                  <td>{highlightText(bundle.name, searchValue)}</td>
                  <td className={classes.descriptionCell} title={bundle.description}>
                    {bundle.description}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {(() => {
                      // Fixed items are applied once per measurement, so they are shown apart from the hourly rate
                      const hourlyRate = bundle.priceBundleItems.reduce((sum, item) => (item.isFixed ? sum : sum + item.price * item.multiplier), 0);
                      const fixedRate = bundle.priceBundleItems.reduce((sum, item) => (item.isFixed ? sum + item.price * item.multiplier : sum), 0);
                      const hourlyText = formatPrice(bundle.currencySymbol, hourlyRate);
                      if (fixedRate <= 0) return hourlyText;
                      return `${hourlyText} + ${formatPrice(bundle.currencySymbol, fixedRate)} ${t('priceBundle.fixedSuffix')}`;
                    })()}
                  </td>
                  <td>{formatTypeCell(bundle.costBundleTypeId, bundle.costBundleTypeName)}</td>
                  <td>{formatAccountCell(bundle.account)}</td>
                  <td>{bundle.companyName}</td>
                  <td>{formatDate(bundle.updatedOn)}</td>
                  <td>
                    <div className={classes.buttonContainer}>
                      <button className={classes.iconButton} onClick={() => handleViewBundle(bundle.uniqueId)}>
                        <Eye className={classes.iconButton__icon} />
                      </button>
                      <button className={classes.iconButton} onClick={() => handleEditBundle(bundle.uniqueId)}>
                        <Pencil className={classes.iconButton__icon} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" style={{ textAlign: "center", padding: "32px" }}>
                  {t('financialCenter.noPriceBundles')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </React.Fragment>
  );
}

export default PriceBundleTable;

const useStyles = makeStyles({
  tablesContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "32px",
  },

  header: {
    width: "100%",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    marginBottom: "16px",
  },

  errorContainer: {
    width: "100%",
    backgroundColor: "#FDF3F3",

    border: "1px solid #FBE5E5",
    borderRadius: "6px",

    marginBottom: "16px",
    padding: "12px",
  },

  errorMessage: {
    fontSize: "16px",
    color: "#D84949",
  },

  buttonContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "8px",
  },

  iconButton: {
    height: "32px",
    width: "32px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: "4px",
    border: "none",

    backgroundColor: "#F8F8F8",

    "&:hover": {
      backgroundColor: "#EDEDED",
    },
  },

  iconButton__icon: {
    height: "20px",
    width: "20px",

    display: "block",
  },

  tableContainer: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    borderSpacing: "0",

    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
  },

  table__header: {
    backgroundColor: "#DFDFDF",
    borderRadius: "8px 8px 0px 0px",

    "& th": {
      height: "36px",
      padding: "0px 16px",
      textAlign: "left",
      fontSize: "16px",
      fontWeight: "normal",
      color: "#0C0E29",

      "&:first-child": {
        borderTopLeftRadius: "8px",
      },

      "&:last-child": {
        borderTopRightRadius: "8px",
        width: "0px",
        padding: "0px",
      },
    },
  },

  table__body: {
    "& tr": {
      borderBottom: "1px solid #EDEDED",

      "&:last-child": {
        borderBottom: "none",

        "& td": {
          "&:first-child": {
            borderBottomLeftRadius: "8px",
          },

          "&:last-child": {
            borderBottomRightRadius: "8px",
          },
        },
      },

      "& td": {
        height: "48px",
        padding: "0px 16px",
        textAlign: "left",
        fontSize: "16px",
        fontWeight: "normal",
        color: "#0C0E29",

        whiteSpace: "nowrap", // Prevent wrapping for all cells
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: 200, // Default max width for all cells

        borderRight: "1px solid #EDEDED",

        "&:last-child": {
          borderRight: "none",
          width: "fit-content",
          padding: "8px",
        },
      },
    },
  },

  descriptionCell: {
    maxWidth: 300,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    cursor: "pointer",
  },

  table__row: {
    backgroundColor: "#FFFFFF !important",
  },
});
